import { useRef, useState, useEffect, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Divider } from '@mui/material';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { useGetUserScheduleEvents } from 'src/modules/profile/services';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';

import { EventItem } from './event-item';

// ----------------------------------------------------------------------

// LocalStorage key for events
const EVENTS_READ_KEY = 'events-read-status';

const EVENT_TABS = [
  { value: 'all', label: 'All', count: 0 },
  { value: 'unread', label: 'Unread', count: 0 },
  { value: 'upcoming', label: 'Upcoming', count: 0 },
];

// ----------------------------------------------------------------------

export function EventsContent() {
  const twoDaysAgoUtc = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const fiveDaysLaterUtc = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  // Get Events from schedule
  const { scheduleEvents } = useGetUserScheduleEvents(twoDaysAgoUtc, fiveDaysLaterUtc);

  // Event tab state and data
  const [currentEventTab, setCurrentEventTab] = useState('all');
  const [events, setEvents] = useState([]);
  const eventsInitializedRef = useRef(false);

  const handleChangeEventTab = useCallback((event, newValue) => {
    setCurrentEventTab(newValue);
  }, []);

  const sortEvents = useCallback((newEvents) => {
    setEvents(
      [...newEvents].sort((a, b) => {
        // Unread first (IsRead=false comes first)
        if (!a.IsRead && b.IsRead) return -1;
        if (a.IsRead && !b.IsRead) return 1;
        return new Date(b.Date) - new Date(a.Date);
      })
    );
  }, []);

  // localStorage functions for events
  const getEventsReadStatusMap = useCallback(() => {
    try {
      const storedReadStatus = localStorage.getItem(EVENTS_READ_KEY);
      return storedReadStatus ? JSON.parse(storedReadStatus) : {};
    } catch (error) {
      console.error('Error loading events from localStorage:', error);
      return {};
    }
  }, []);

  const saveEventsReadStatusMap = useCallback((readStatusMap) => {
    try {
      localStorage.setItem(EVENTS_READ_KEY, JSON.stringify(readStatusMap));
      window.dispatchEvent(new Event('localStorageUpdated'));
    } catch (error) {
      console.error('Error saving events to localStorage:', error);
    }
  }, []);

  // Initialize events from scheduleEvents
  useEffect(() => {
    if (!scheduleEvents || scheduleEvents.length === 0) return;

    const readStatusMap = getEventsReadStatusMap();

    const initializedEvents = scheduleEvents.map((event) => {
      const hasStoredValue = readStatusMap[event.Id] !== undefined;

      return {
        ...event,
        IsRead: hasStoredValue ? readStatusMap[event.Id] : false,
      };
    });

    sortEvents(initializedEvents);
    eventsInitializedRef.current = true;
  }, [scheduleEvents, getEventsReadStatusMap, sortEvents]);

  // Save events read status to localStorage
  useEffect(() => {
    if (!eventsInitializedRef.current || events.length === 0) return;

    const readStatusMap = {};
    events.forEach((event) => {
      readStatusMap[event.Id] = event.IsRead;
    });
    saveEventsReadStatusMap(readStatusMap);
  }, [events, saveEventsReadStatusMap]);

  // Events counts
  const eventsUnreadCount = events.filter((item) => !item.IsRead).length;
  const eventsUpcomingCount = events.filter((item) => {
    const eventDate = new Date(item.Date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  }).length;

  // Event tabs with counts
  const eventTabs = [
    { value: 'all', label: 'All', count: events.length },
    { value: 'unread', label: 'Unread', count: eventsUnreadCount },
    { value: 'upcoming', label: 'Upcoming', count: eventsUpcomingCount },
  ];

  // Events handlers
  const handleMarkAllEventsAsRead = useCallback(() => {
    sortEvents(events.map((event) => ({ ...event, IsRead: true })));
  }, [events, sortEvents]);

  const handleMarkEventAsRead = useCallback(
    (eventId) => {
      sortEvents(events.map((e) => (e.Id === eventId ? { ...e, IsRead: true } : e)));
    },
    [events, sortEvents]
  );

  const handleResetEvents = useCallback(() => {
    try {
      localStorage.removeItem(EVENTS_READ_KEY);
      sortEvents(events.map((event) => ({ ...event, IsRead: false })));
    } catch (error) {
      console.error('Error resetting events:', error);
    }
  }, [events, sortEvents]);

  // Filter events based on current tab
  const filteredEvents = events.filter((event) => {
    if (currentEventTab === 'unread') return !event.IsRead;
    if (currentEventTab === 'upcoming') {
      const eventDate = new Date(event.Date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    }
    return true; // 'all' tab
  });

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Events
      </Typography>

      {!!eventsUnreadCount && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllEventsAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Reset all events">
        <IconButton onClick={handleResetEvents} color="default">
          <Iconify icon="solar:restart-bold-duotone" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderEventTabs = (
    <CustomTabs variant="fullWidth" value={currentEventTab} onChange={handleChangeEventTab}>
      {eventTabs.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={
                ((tab.value === 'all' || tab.value === currentEventTab) && 'filled') || 'soft'
              }
              color={
                (tab.value === 'unread' && 'error') ||
                (tab.value === 'upcoming' && 'info') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderEventsList = (
    <Scrollbar>
      <Box component="ul">
        {filteredEvents.length > 0 ? (
          filteredEvents.map((event) => (
            <Box component="li" key={event.Id} sx={{ display: 'flex' }}>
              <EventItem
                notification={event}
                onMarkAsRead={handleMarkEventAsRead}
                isUnReadField="IsRead"
              />
            </Box>
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No events found
            </Typography>
          </Box>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      {renderHead}
      <Divider sx={{ mt: 1 }} />
      {renderEventTabs}
      {renderEventsList}
    </>
  );
}
